import { rawOAuth } from "../../utils";
import {
  BulkMockupGenerationPayload,
  BulkMockupJobStatus,
  BulkMockupJobStatusResponse,
} from "./types";
import { makeMultiJobStorage, makeJobStorage, pollUntilTerminal } from "./utils";

const bulkMockupJobStorage = makeMultiJobStorage("bulk_mockup_jobs:");
const bulkMockupJobItemsStorage = makeJobStorage("bulk_mockup_job_items:");

export function persistBulkJobItems(bulkJobId: string, itemIds: string[]): void {
  bulkMockupJobItemsStorage.persist(bulkJobId, JSON.stringify(itemIds));
}

export function readBulkJobItems(bulkJobId: string): string[] {
  const raw = bulkMockupJobItemsStorage.read(bulkJobId);
  if (!raw) return [];
  try { return JSON.parse(raw) as string[]; } catch { return []; }
}

// Keyed by bulkJobId to prevent duplicate polling of the same job.
const inFlightBulkByJobId = new Map<string, Promise<BulkMockupJobStatus>>();

async function getBulkJobStatus(bulkJobId: string): Promise<BulkMockupJobStatusResponse> {
  const res = await rawOAuth(
    "GET",
    "mockup-generation",
    { bulk_job_id: bulkJobId },
    null,
    { Accept: "application/json" },
  );
  const json = (await res.json()) as BulkMockupJobStatusResponse;
  if (!res.ok) {
    return { status: "failed", bulk_job_id: bulkJobId, error: json?.error || "Unable to fetch bulk mockup status" };
  }
  return json;
}

/**
 * Enqueue a bulk AI mockup generation job on the PHP backend.
 * Returns immediately with a job ID — all orchestration runs in a background worker.
 */
export async function startBulkMockupGeneration(
  config: BulkMockupGenerationPayload,
): Promise<{ bulk_job_id: string }> {
  const readyIds = config.readyItems.map((i) => i.item_id).filter(Boolean);
  const decoratedIds = config.decoratedItems.map((i) => i.item_id).filter(Boolean);
  if (readyIds.length === 0 && decoratedIds.length === 0) {
    throw new Error("Please select at least one item to generate mockups for.");
  }
  const payload = {
    actionName: "bulk_generate",
    ready_item_ids: readyIds,
    decorated_item_ids: decoratedIds,
    artwork_url: config.artworkUrl,
    artwork_file_id: config.artworkFileId ?? "",
    proof_id: config.proofId,
    additional_instructions: config.additionalInstructions,
    decoration_save_mode: config.decorationSaveMode,
    existing_instructions_mode: config.existingInstructionsMode,
    ready_items_with_item_image_id: JSON.stringify(config.readyItemsWithItemImageId ?? {}),
    decorated_items_with_item_image_id: JSON.stringify(config.decoratedItemsWithItemImageId ?? {}),
  };
  const res = await rawOAuth("POST", "mockup-generation", null, payload, { Accept: "application/json" });
  const json = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error(String(json?.error ?? "Failed to start bulk mockup generation"));
  }
  return json as { bulk_job_id: string };
}

/**
 * Poll a bulk mockup job until completion. Persists the job ID in localStorage
 * (keyed by `parentId`) alongside any other active jobs, so all can be resumed
 * across navigation via `resumeBulkMockupJob`.
 *
 * Deduplicates by `bulkJobId` — calling this twice for the same job returns the
 * same in-flight promise.
 */
export async function pollBulkMockupJob(args: {
  bulkJobId: string;
  parentId: string;
  onStatusChange: (status: BulkMockupJobStatus, response: BulkMockupJobStatusResponse) => void;
  timeoutMs?: number;
}): Promise<BulkMockupJobStatus> {
  const { bulkJobId, parentId, onStatusChange, timeoutMs = 600000 } = args;

  const existing = inFlightBulkByJobId.get(bulkJobId);
  if (existing) return existing;

  const promise = (async () => {
    bulkMockupJobStorage.persist(parentId, bulkJobId);

    const polled = await pollUntilTerminal(
      async () => {
        const resp = await getBulkJobStatus(bulkJobId);
        try {
          onStatusChange(resp.status, resp);
        } catch (callbackErr) {
          console.error("[pollBulkMockupJob] onStatusChange threw:", callbackErr);
        }
        return resp;
      },
      () => {
        const timedOut: BulkMockupJobStatusResponse = { status: "failed", bulk_job_id: bulkJobId, error: "Bulk mockup generation timed out" };
        onStatusChange("failed", timedOut);
        return timedOut;
      },
      { timeoutMs, initialPollMs: 5000 },
    );

    bulkMockupJobStorage.remove(parentId, bulkJobId);
    bulkMockupJobItemsStorage.clear(bulkJobId);
    return polled.status;
  })().finally(() => {
    inFlightBulkByJobId.delete(bulkJobId);
  });

  inFlightBulkByJobId.set(bulkJobId, promise);
  return promise;
}

/**
 * Returns the job IDs currently persisted in localStorage for `parentId`.
 * Useful for eagerly setting UI state before calling `resumeBulkMockupJob`.
 */
export function readPersistedBulkJobIds(parentId: string): string[] {
  return bulkMockupJobStorage.read(parentId);
}

/**
 * Resume all bulk mockup jobs persisted in localStorage for `parentId`.
 *
 * `makeStatusHandler` is called once per job (both already-terminal and
 * still-running) so the caller can create a dedicated handler that tracks
 * per-job state (e.g. a "job finished" flag). For terminal jobs the handler
 * is invoked once immediately; for running jobs it drives the polling loop.
 *
 * Returns null if no persisted jobs exist for `parentId`.
 */
export async function resumeBulkMockupJob(args: {
  parentId: string;
  makeStatusHandler: () => (status: BulkMockupJobStatus, response: BulkMockupJobStatusResponse) => void;
  timeoutMs?: number;
}): Promise<BulkMockupJobStatus | null> {
  const { parentId, makeStatusHandler, timeoutMs = 600000 } = args;

  const jobIds = bulkMockupJobStorage.read(parentId);
  if (jobIds.length === 0) return null;

  const results = await Promise.all(
    jobIds.map(async (bulkJobId) => {
      const onStatusChange = makeStatusHandler();

      // If already polling (e.g. the component remounted), reuse the promise.
      const inFlight = inFlightBulkByJobId.get(bulkJobId);
      if (inFlight) return inFlight;

      // Check current status before deciding whether to poll.
      const current = await getBulkJobStatus(bulkJobId);
      if (current.status === "completed" || current.status === "failed") {
        bulkMockupJobStorage.remove(parentId, bulkJobId);
        onStatusChange(current.status, current);
        return current.status;
      }

      // Still running — notify immediately so the banner appears, then poll.
      onStatusChange(current.status, current);
      return pollBulkMockupJob({ bulkJobId, parentId, onStatusChange, timeoutMs });
    }),
  );

  return results[results.length - 1] ?? null;
}
